import { MutationCache, QueryClient } from "@tanstack/react-query";
import { ApiClientError, apiRequest } from "./api";
import { beginUserAction } from "./correlation";

/**
 * TanStack Query defaults (API-STANDARD.md §6).
 *
 * Idempotent GETs retry three times with exponential backoff; mutations are
 * never auto-retried, because a retried POST can start a second run. A mutation
 * that IS safe to retry carries an `Idempotency-Key` header and retries at the
 * call site, not here.
 *
 * 4xx is never retried: the request will fail the same way in 30 seconds, and
 * retrying a 403 just writes three more `auth.denied` audit rows.
 */
const MAX_QUERY_ATTEMPTS = 3;

function isWorthRetrying(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) return true;
  if (error.retryable) return true;
  return error.status === 0 || error.status >= 500 || error.status === 429;
}

/** The default `retry` of every query; `listQueryOptions` (./listQuery.ts) builds on it. */
export function retryQuery(failureCount: number, error: unknown): boolean {
  return failureCount < MAX_QUERY_ATTEMPTS && isWorthRetrying(error);
}

/**
 * The default query function: a query key whose first element is a path string
 * is fetched through `apiRequest`, so every query gets the auth, instance,
 * surface and correlation headers without restating them.
 *
 *   useQuery({ queryKey: ["/api/v2/connections"] })
 */
export const queryClient = new QueryClient({
  // #1245: every mutation is an explicit user action (a save, a validate, a run), so it starts
  // its own correlation id before its request goes (API-STANDARD.md §4). Without it a screen's
  // writes shared the id its navigation minted, and two different refusals on one form printed
  // one reference id, so support could not tell them apart. A cache callback runs before the
  // mutation's own `onMutate` and its request.
  mutationCache: new MutationCache({
    onMutate: () => {
      beginUserAction();
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: async ({ queryKey, signal }) => {
        const [path] = queryKey;
        if (typeof path !== "string") {
          throw new Error("queryKey[0] must be the request path");
        }
        return apiRequest(path, { signal });
      },
      retry: retryQuery,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15_000),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
    mutations: {
      retry: false,
    },
  },
});
